import {
  useDeleteCommunityReview,
  useReportCommunityReview,
} from '@/actions/community-review.actions.js';
import useUserProfile from '@/hooks/useUserProfile';
import { Menu, Transition } from '@headlessui/react';
import { cx } from 'cva';
import { LucideFlag } from 'lucide-react';
import React, { Fragment } from 'react';
import { HiDotsHorizontal } from 'react-icons/hi';
import { MdDeleteOutline, MdOutlineEditNote } from 'react-icons/md';
import useConfirmDialogStore from '../../Dialogs/confirmDialog.store';
import { useGetCommunityRating } from '@/actions/nonprofit.actions';
import { useIsNonProfitAdminFor } from '@/hooks/useUser';
import useHasUserSession from '@/hooks/useHasUserSession';

const confirmTitleReview = {
  delete: 'Delete Review',
  report: 'Report Review',
};

const confirmMessageReview = {
  delete:
    'Are you sure you want to delete this review? This action cannot be undone.',
  report:
    'If you report this review, an email will be sent to the admin and we may reach out to you to better understand this issue. Are you sure you want to report this review?',
};

const confirmTitleReply = {
  delete: 'Delete Comment',
  report: 'Report Comment',
};

const confirmMessageReply = {
  delete:
    'Are you sure you want to delete this comment? This action cannot be undone.',
  report:
    'If you report this comment, an email will be sent to the admin and we may reach out to you to better understand this issue. Are you sure you want to report this comment?',
};

function CommunityReviewMenu(props) {
  const { review, onEdit, nonProfit, handleRedirect } = props;

  const user = useUserProfile();
  const { seekConfirmation } = useConfirmDialogStore();
  const isAdminOfNPO = useIsNonProfitAdminFor(review?.nonProfitId);

  const hasUserSession = useHasUserSession();

  // mutations
  const { mutateAsync: deleteReview } = useDeleteCommunityReview();
  const { mutateAsync: reportReview } = useReportCommunityReview();
  const { refetch: refetchNonProfitRating } = useGetCommunityRating(
    review?.nonProfitId,
  );

  // derived states
  const isAuthor = review?.authorId === user?.id;
  const isReply = !!review?.parentReviewId;

  const confirmTitle = isReply ? confirmTitleReply : confirmTitleReview;
  const confirmMessage = isReply ? confirmMessageReply : confirmMessageReview;

  // event handlers
  const handleDelete = async () => {
    await deleteReview(review);
    await refetchNonProfitRating();
  };

  const handleReport = async () => {
    await reportReview(review);
  };

  const handleConfirm = async (action) => {
    const confirmed = await seekConfirmation(
      confirmTitle[action],
      confirmMessage[action],
      'Continue',
    );

    if (confirmed) {
      switch (action) {
        case 'delete':
          handleDelete();
          break;
        case 'report':
          handleReport();
          break;
        default:
          break;
      }
    }
  };

  // render
  return (
    <>
      <Menu as="div" className="relative inline-block text-left">
        <div>
          {hasUserSession ? (
            <Menu.Button className="rounded-full bg-transparent text-text-disabled hover:font-semibold hover:text-text-primary active:text-text-primary">
              <HiDotsHorizontal aria-hidden="true" />
            </Menu.Button>
          ) : (
            <div
              onClick={() => handleRedirect()}
              className="cursor-pointer rounded-full bg-transparent text-text-disabled hover:font-semibold hover:text-text-primary active:text-text-primary">
              <HiDotsHorizontal aria-hidden="true" />
            </div>
          )}
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95">
          <Menu.Items className="absolute right-0 z-1000 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="px-1 py-1 ">
              {isAuthor && (
                <>
                  {isReply && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={onEdit}
                          className={cx(
                            'group flex w-full items-center rounded-md p-2.5 font-poppins text-14',
                            active
                              ? 'bg-primary font-semibold text-white'
                              : 'text-text-primary',
                          )}>
                          <MdOutlineEditNote
                            className="mr-2 h-5 w-5"
                            aria-hidden="true"
                          />
                          Edit
                        </button>
                      )}
                    </Menu.Item>
                  )}

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        className={cx(
                          'group flex w-full items-center rounded-md p-2.5 font-poppins text-14 font-normal',
                          active
                            ? 'bg-primary font-semibold text-white'
                            : 'text-text-primary',
                        )}
                        onClick={() => handleConfirm('delete')}>
                        <MdDeleteOutline
                          className="mr-2 h-5 w-5"
                          aria-hidden="true"
                        />
                        Delete this {isReply ? 'reply' : 'review'}
                      </button>
                    )}
                  </Menu.Item>
                </>
              )}
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    className={cx(
                      'group flex w-full items-center rounded-md p-2.5 font-poppins text-14 font-normal',
                      active
                        ? 'bg-primary font-semibold text-white'
                        : 'text-text-primary',
                    )}
                    onClick={() => handleConfirm('report')}>
                    <LucideFlag className="mr-2 h-5 w-5" aria-hidden="true" />
                    Report this {isReply ? 'reply' : 'review'}
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </>
  );
}

export default CommunityReviewMenu;
